import React, { useEffect } from 'react';
import { captureException } from '@sentry/react';
import { isPast } from 'date-fns';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { usePlayerTrackInfo } from '@components/AudioPlayer/usePlayerTrackInfo';
import { Waveform } from '@components/Waveform/Release/Waveform';
import { Hero } from '@components/basics/Hero';
import {
  HeroDetailsMobile,
  HeroInfoWrapper,
  HeroPlayInfoWrapper,
  HeroTitle,
} from '@components/basics/Hero/shared';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  MinimalUserFragmentDoc,
  PlaylistType,
  SongHeroFragmentDoc,
} from '@graphql/reactQuery';
import { usePlayButton } from '@hooks/usePlayButton';
import { trackEvent } from '@lib/analytics';
import { loadTrack } from '@store/audio/AudioController';
import { prefetchAudioFile } from '@store/audio/AudioFetcher';
import { getAnimatedGoldenEggImage } from '@utils/helpers';
import { AnimatedCountdown } from './AnimatedCountdown';
import { SongHeroCoverImages } from './SongHeroCoverImages';
import { SongHeroDetails } from './SongHeroDetails';
import { SongContainer, WaveformContainer } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SongHero on Release {
    id
    title
    type
    mintStartTimestamp
    listeningParty
    artist {
      id
      name
      webappUri
      user {
        ...MinimalUser
      }
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    track {
      id
      duration
      normalizedPeaks
    }
    goldenEggImage {
      id
      url
    }
    animatedGoldenEggImageOptimized {
      id
      url
    }
    ...WaveformWithComments
    ...SongHeroCoverImages
    ...GoldenEggCoverBadge
    ...SongHeroDetails
  }
`);

export function SongHero({ release }: { release: FragmentType<SongHeroFragmentDoc> }) {
  const releaseData = getFragment(SongHeroFragmentDoc, release);
  const userData = getFragment(MinimalUserFragmentDoc, releaseData.artist.user);
  const {
    artist,
    coverImage,
    staticCoverImage,
    title,
    goldenEggImage,
    animatedGoldenEggImageOptimized,
    mintStartTimestamp,
    listeningParty,
  } = releaseData;

  const isAlbum = releaseData.type === 'ALBUM';

  const {
    onPlayClick,
    isLoading,
    isPlaying,
    ref: playButtonPrefetchRef,
    currentListeningParty,
  } = usePlayButton({
    isAlbum,
    trackId: releaseData.track.id,
    playlist: { associationId: artist.id, type: PlaylistType.Artist },
    listeningParty: releaseData.listeningParty,
    releaseId: releaseData.id,
    onListeningPartyStart() {
      loadTrack({
        trackId: releaseData.track.id,
      }).catch(captureException);

      trackEvent({
        event: 'Joined Listening Party',
        properties: {
          associationType: 'Release',
          associationId: releaseData.id,
          sourceComponent: 'Song Page',
        },
      });
    },
  });

  const isAudioEnabled = !isAlbum && !currentListeningParty;

  // Prefetch the track in view
  const playerTrackInfo = usePlayerTrackInfo({
    trackId: releaseData.track.id,
    shouldFetch: !!releaseData.track.id,
  });

  const { trackAudioUrl } = playerTrackInfo;

  useEffect(() => {
    if (!trackAudioUrl) return;

    prefetchAudioFile({
      url: trackAudioUrl,
    });
  }, [trackAudioUrl]);

  const hideCountdown =
    isPast(mintStartTimestamp) || (listeningParty != null && !currentListeningParty);

  const goldenEggImageUrl = getAnimatedGoldenEggImage({
    goldenEggImage: goldenEggImage?.url,
    animatedGoldenEggImage: animatedGoldenEggImageOptimized?.url,
  });

  const showGoldenEggToggle = !!goldenEggImageUrl && hideCountdown;

  const rightContent = (
    <>
      <SongContainer>
        <HeroPlayInfoWrapper>
          <PlayButton
            playing={isPlaying}
            loading={isLoading}
            onClick={onPlayClick}
            color="white"
            size="xl"
            disabled={!isAudioEnabled}
            ref={playButtonPrefetchRef}
            currentListeningParty={currentListeningParty}
          />
          <HeroInfoWrapper>
            <HeroTitle title={title} />
            <SongHeroDetails releaseFrag={releaseData} />
          </HeroInfoWrapper>
        </HeroPlayInfoWrapper>

        <AnimatedCountdownContainer>
          <AnimatedCountdown
            {...{
              mintStartTimestamp,
              hideCountdown,
              userId: userData.id,
              artistId: artist.id,
              currentListeningParty,
              listeningParty,
            }}
          />
        </AnimatedCountdownContainer>
      </SongContainer>
      <HeroDetailsMobile>
        <HeroTitle title={title} />
        <SongHeroDetails releaseFrag={releaseData} />
      </HeroDetailsMobile>
    </>
  );

  return (
    <Hero
      backgroundImageSrc={staticCoverImage?.url || coverImage.url}
      cover={
        <>
          <SongHeroCoverImages
            showGoldenEggToggle={showGoldenEggToggle}
            hideCountdown={hideCountdown}
            release={releaseData}
          />
          <AnimatedCountdownContainer mobile>
            <AnimatedCountdown
              small
              {...{
                mintStartTimestamp,
                hideCountdown,
                userId: userData.id,
                artistId: artist.id,
                listeningParty,
              }}
              currentListeningParty={currentListeningParty}
            />
          </AnimatedCountdownContainer>
        </>
      }
      details={rightContent}
      withBlur
      variant="song"
    >
      <WaveformContainer>
        <Waveform
          type="withComments"
          releaseFrag={releaseData}
          isTrackDisabled={!isAudioEnabled}
          showBottomHalf
          enableTouchHandler
        />
      </WaveformContainer>
    </Hero>
  );
}

const AnimatedCountdownContainer = styled('div', {
  variants: {
    mobile: {
      true: {
        position: 'absolute',
        top: 75,
        width: '100%',
        '@tablet': {
          display: 'none',
        },
      },
      false: {
        display: 'none',
        '@tablet': {
          display: 'flex',
        },
      },
    },
  },
});
